import { useRef, type PointerEvent } from 'react';

// Only the handle and header drag: the body scrolls, and a drag there would fight the scroll.
const DRAG_ZONE = '[data-slot=sheet-drag-handle], [data-slot=sheet-header]';
const INTERACTIVE = 'a, button, input, select, textarea, [role=button]';
// A release past a quarter of the sheet's height, or a downward flick, closes it.
const CLOSE_DISTANCE_RATIO = 0.25;
const CLOSE_VELOCITY_PX_PER_MS = 0.5;
const FLICK_MAX_PAUSE_MS = 100;

type Drag = { pointerId: number; startY: number; lastY: number; lastTime: number; velocity: number };

/**
 * Pointer handlers for a bottom sheet's content that let the customer drag it down to close it.
 * The offset uses the CSS `translate` property, which composes with the `transform` of the exit
 * animation, so a released sheet slides out from where the finger let go.
 */
export const useSwipeToClose = ({ enabled, onClose }: { enabled: boolean; onClose: () => void }) => {
  const drag = useRef<Drag | null>(null);

  if (!enabled) {
    return {};
  }

  const release = (element: HTMLElement, close: boolean) => {
    drag.current = null;
    if (close) {
      onClose();
      return;
    }
    element.style.transition = 'translate 200ms ease-out';
    element.style.translate = '';
  };

  return {
    onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
      const target = event.target as Element;
      if (event.button !== 0 || !target.closest(DRAG_ZONE) || target.closest(INTERACTIVE)) {
        return;
      }
      event.currentTarget.setPointerCapture(event.pointerId);
      drag.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        lastY: event.clientY,
        lastTime: event.timeStamp,
        velocity: 0
      };
    },
    onPointerMove: (event: PointerEvent<HTMLDivElement>) => {
      const current = drag.current;
      if (current?.pointerId !== event.pointerId) {
        return;
      }
      const elapsed = event.timeStamp - current.lastTime;
      if (elapsed > 0) {
        current.velocity = (event.clientY - current.lastY) / elapsed;
      }
      current.lastY = event.clientY;
      current.lastTime = event.timeStamp;
      // The sheet follows the finger down only; dragging up past its resting place does nothing.
      const offset = Math.max(0, event.clientY - current.startY);
      event.currentTarget.style.transition = 'none';
      event.currentTarget.style.translate = `0 ${offset}px`;
    },
    onPointerUp: (event: PointerEvent<HTMLDivElement>) => {
      const current = drag.current;
      if (current?.pointerId !== event.pointerId) {
        return;
      }
      const offset = current.lastY - current.startY;
      const isPastDistance = offset > event.currentTarget.offsetHeight * CLOSE_DISTANCE_RATIO;
      // A finger that stopped before letting go is not flicking, whatever speed it last moved at.
      const isStill = event.timeStamp - current.lastTime > FLICK_MAX_PAUSE_MS;
      const isFlick = offset > 0 && !isStill && current.velocity > CLOSE_VELOCITY_PX_PER_MS;
      release(event.currentTarget, isPastDistance || isFlick);
    },
    onPointerCancel: (event: PointerEvent<HTMLDivElement>) => {
      if (drag.current?.pointerId === event.pointerId) {
        release(event.currentTarget, false);
      }
    }
  };
};
